import React, { useContext } from "react";
import { Input } from "@fluentui/react-components";
import type { Element } from "../../../services/PowderFormService";
import { PowderContext } from "../redux/ReduxContext";
import { TargetPercent } from "../redux/powderOrder";

const CATEGORY_COLORS = {
    alkaliMetal: "#ff6b6b",
    alkalineEarthMetal: "#ffa94d",
    transitionMetal: "#74c0fc",
    postTransitionMetal: "#adb5bd",
    metalloid: "#63e6be",
    nonmetal: "#8ce99a",
    halogen: "#e599f7",
    nobleGas: "#ffd43b",
    lanthanide: "#f783ac",
    actinide: "#da77f2",
    unknown: "#6c757d",
};

const ALKALI_METALS = new Set(["Li", "Na", "K", "Rb", "Cs", "Fr"]);
const ALKALINE_EARTH_METALS = new Set(["Be", "Mg", "Ca", "Sr", "Ba", "Ra"]);
const TRANSITION_METALS = new Set(["Sc", "Ti", "V", "Cr", "Mn", "Fe", "Co", "Ni", "Cu", "Zn", "Y", "Zr", "Nb", "Mo", "Tc", "Ru", "Rh", "Pd", "Ag", "Cd", "Hf", "Ta", "W", "Re", "Os", "Ir", "Pt", "Au", "Hg", "Rf", "Db", "Sg", "Bh", "Hs", "Mt", "Ds", "Rg", "Cn"]);
const POST_TRANSITION_METALS = new Set(["Al", "Ga", "In", "Tl", "Sn", "Pb", "Bi", "Fl", "Lv"]);
const METALLOIDS = new Set(["B", "Si", "Ge", "As", "Sb", "Te", "Po"]);
const NONMETALS = new Set(["H", "C", "N", "O", "P", "S", "Se"]);
const HALOGENS = new Set(["F", "Cl", "Br", "I", "At", "Ts"]);
const NOBLE_GASES = new Set(["He", "Ne", "Ar", "Kr", "Xe", "Rn", "Og"]);
const LANTHANIDES = new Set(["La", "Ce", "Pr", "Nd", "Pm", "Sm", "Eu", "Gd", "Tb", "Dy", "Ho", "Er", "Tm", "Yb", "Lu"]);
const ACTINIDES = new Set(["Ac", "Th", "Pa", "U", "Np", "Pu", "Am", "Cm", "Bk", "Cf", "Es", "Fm", "Md", "No", "Lr"]);

export const getElementBorderColor = (symbol: string): string => {
    if (ALKALI_METALS.has(symbol)) return CATEGORY_COLORS.alkaliMetal;
    if (ALKALINE_EARTH_METALS.has(symbol)) return CATEGORY_COLORS.alkalineEarthMetal;
    if (TRANSITION_METALS.has(symbol)) return CATEGORY_COLORS.transitionMetal;
    if (POST_TRANSITION_METALS.has(symbol)) return CATEGORY_COLORS.postTransitionMetal;
    if (METALLOIDS.has(symbol)) return CATEGORY_COLORS.metalloid;
    if (NONMETALS.has(symbol)) return CATEGORY_COLORS.nonmetal;
    if (HALOGENS.has(symbol)) return CATEGORY_COLORS.halogen;
    if (NOBLE_GASES.has(symbol)) return CATEGORY_COLORS.nobleGas;
    if (LANTHANIDES.has(symbol)) return CATEGORY_COLORS.lanthanide;
    if (ACTINIDES.has(symbol)) return CATEGORY_COLORS.actinide;
    return CATEGORY_COLORS.unknown;
};

interface ElementPickerModalProps {
    open: boolean;
    allElements: Element[];
    elementSearch: string;
    setElementSearch: (s: string) => void;
    selectedElements?: string[];
    onToggleElement?: (symbol: string) => void;
}

export default function ElementPickerModal({
    allElements,
    elementSearch,
    setElementSearch,
    selectedElements,
    onToggleElement,
}: ElementPickerModalProps) {
    const powder = useContext(PowderContext);
    const state = powder?.state;
    const dispatch = powder?.dispatch;
    const effectiveSelectedElements = selectedElements ?? state?.selectedElements ?? [];

    const onButtonClick = (symbol: string) => {
        if (onToggleElement) {
            onToggleElement(symbol);
            return;
        }

        if (!state || !dispatch) {
            return;
        }

        if (state.selectedElements.includes(symbol)) {
            dispatch({
                type: "DELETE_ARRAY_ITEM",
                payload: { field: "selectedElements", index: state.selectedElements.indexOf(symbol) }
            });
            dispatch({
                type: "DELETE_ARRAY_ITEM",
                payload: { field: "targetPercents", index: state.targetPercents?.findIndex(t => t.symbol === symbol) ?? -1 }
            });
        } else {
            dispatch({
                type: "ADD_ARRAY_ITEM_LAST",
                payload: { field: "selectedElements", item: symbol }
            });
            const target: TargetPercent = { symbol, value: null, displayValue: "" };
            dispatch({
                type: "ADD_ARRAY_ITEM_LAST",
                payload: { field: "targetPercents", item: target }
            });
        }
    };

    const tableRows: (string | null)[][] = [
        ["H", ...Array(16).fill(null), "He"],
        ["Li", "Be", ...Array(10).fill(null), "B", "C", "N", "O", "F", "Ne"],
        ["Na", "Mg", ...Array(10).fill(null), "Al", "Si", "P", "S", "Cl", "Ar"],
        ["K", "Ca", "Sc", "Ti", "V", "Cr", "Mn", "Fe", "Co", "Ni", "Cu", "Zn", "Ga", "Ge", "As", "Se", "Br", "Kr"],
        ["Rb", "Sr", "Y", "Zr", "Nb", "Mo", "Tc", "Ru", "Rh", "Pd", "Ag", "Cd", "In", "Sn", "Sb", "Te", "I", "Xe"],
        ["Cs", "Ba", null, "Hf", "Ta", "W", "Re", "Os", "Ir", "Pt", "Au", "Hg", "Tl", "Pb", "Bi", "Po", "At", "Rn"],
        ["Fr", "Ra", null, "Rf", "Db", "Sg", "Bh", "Hs", "Mt", "Ds", "Rg", "Cn", "Fl", "Lv", "Ts", "Og", null, null],
        Array(18).fill(null),
        [null, null, "La", "Ce", "Pr", "Nd", "Pm", "Sm", "Eu", "Gd", "Tb", "Dy", "Ho", "Er", "Tm", "Yb", "Lu", null],
        [null, null, "Ac", "Th", "Pa", "U", "Np", "Pu", "Am", "Cm", "Bk", "Cf", "Es", "Fm", "Md", "No", "Lr", null]
    ];

    const elementMap: Record<string, Element | undefined> = {};
    allElements.forEach(el => elementMap[el.Symbol] = el);

    const searchTerms = elementSearch
        .toLowerCase()
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);

    const gridStyle = {
        gridTemplateColumns: "repeat(18, minmax(0, 1fr))",
        ["--element-cell-size" as string]: "clamp(1.75rem, calc((90vh - 14rem) / 10), 3.25rem)",
    } as React.CSSProperties;

    return (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
            <Input
                size="large"
                placeholder="Search elements (comma-separated for multiple)"
                value={elementSearch}
                onChange={e => setElementSearch(e.target.value)}
                className="mb-4 w-full shrink-0"
            />

            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-2">
                <div className="flex w-full flex-col space-y-0.5">
                    {tableRows.map((row, i) => (
                        <div
                            key={i}
                            className="grid gap-0.5"
                            style={gridStyle}
                        >
                            {row.map((symbol, j) => {
                                if (!symbol) {
                                    return <div key={`empty-${i}-${j}`} className="h-[var(--element-cell-size)] min-w-0" />;
                                }

                                const el = elementMap[symbol];
                                if (!el) return null;

                                const matches =
                                    searchTerms.length === 0 ||
                                    searchTerms.includes(el.Symbol.toLowerCase());

                                const isSelected = effectiveSelectedElements.includes(el.Symbol);
                                const borderColor = getElementBorderColor(el.Symbol);

                                return (
                                    <button
                                        key={el.Symbol}
                                        type="button"
                                        onClick={() => matches && onButtonClick(el.Symbol)}
                                        className={`
                                            group relative flex h-[var(--element-cell-size)] min-w-0 items-center justify-center border transition
                                            ${!matches ? 'cursor-not-allowed opacity-25' : 'cursor-pointer hover:border-[#ffbf39]'}
                                        `}
                                        style={{
                                            borderColor,
                                            backgroundColor: isSelected ? borderColor : 'transparent',
                                            color: isSelected ? '#fff' : borderColor
                                        }}
                                        disabled={!matches}
                                    >
                                        <span className="text-xs font-semibold leading-none sm:text-sm lg:text-base">{el.Symbol}</span>

                                        <span className="bg-primary-dark text-[12px] text-primary-light opacity-0 group-hover:opacity-100 absolute px-1 py-0.5 rounded shadow z-10 mt-8 pointer-events-none">
                                            {el.Name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}